import { checkSupabaseConnection, fetchItemsFromSupabase, fetchRentalsFromSupabase, fetchUsersFromSupabase, supabase } from '../lib/supabase';
import { categories as mockCategories } from '../data/mockData';
import type { Category, Item, Owner, Location, Review, Transaction } from '../types';

// Geocoding function to convert address to coordinates
async function geocodeAddress(location: Location): Promise<Location> {
  try {
    // Build the address string
    const addressParts = [
      location.address,
      location.city,
      location.postcode
    ].filter(Boolean);
    
    if (addressParts.length === 0) {
      throw new Error('No address provided');
    }
    
    const addressString = addressParts.join(', ');
    
    // Add a small delay to respect rate limits (OpenStreetMap allows 1 request per second)
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Use OpenStreetMap Nominatim API (free, no API key required)
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressString)}&countrycodes=gb&limit=1`,
      {
        headers: {
          'User-Agent': 'ShareMyShed/1.0' // Required by Nominatim
        }
      }
    );
    
    if (!response.ok) {
      throw new Error('Geocoding service unavailable');
    }
    
    const data = await response.json();
    
    if (!data || data.length === 0) {
      throw new Error('Address not found. Please check the address, city, and postcode.');
    }
    
    const result = data[0];
    
    // Validate that we got valid coordinates
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    
    if (isNaN(lat) || isNaN(lon)) {
      throw new Error('Invalid coordinates received from geocoding service');
    }
    
    return {
      ...location,
      coordinates: {
        latitude: lat,
        longitude: lon
      }
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    throw error;
  }
}

// Helper function to transform Supabase item data to our Item type
function transformSupabaseItemToItem(supabaseItem: any): Item {
  // Parse location from JSON string if it's a string
  let location;
  try {
    location = typeof supabaseItem.location === 'string' 
      ? JSON.parse(supabaseItem.location) 
      : supabaseItem.location;
  } catch (error) {
    console.error('Error parsing location JSON:', error);
    // Fallback to default location structure
    location = {
      address: '',
      city: '',
      postcode: '',
      coordinates: { latitude: 0, longitude: 0 }
    };
  }

  return {
    id: supabaseItem.id,
    name: supabaseItem.name,
    description: supabaseItem.description || '',
    dailyRateGBP: supabaseItem.daily_rate_gbp,
    images: supabaseItem.images || [],
    ownerId: supabaseItem.owner_id,
    minDays: supabaseItem.min_days || 1,
    maxDays: supabaseItem.max_days || 30,
    location: location,
    category: supabaseItem.category,
    availability: supabaseItem.availability !== false,
    rentalPeriods: [] // We'll populate this from rentals data
  };
}

// Helper function to transform Supabase user data to our Owner type
function transformSupabaseUserToOwner(supabaseUser: any): Owner {
  return {
    id: supabaseUser.id,
    name: supabaseUser.name,
    rating: supabaseUser.rating || 0,
    totalRatings: supabaseUser.total_ratings || 0
  };
}

// Helper function to group items by category
function groupItemsByCategory(items: Item[]): Category[] {
  const categoryMap = new Map<string, Item[]>();

  items.forEach(item => {
    if (!categoryMap.has(item.category)) {
      categoryMap.set(item.category, []);
    }
    categoryMap.get(item.category)!.push(item);
  });

  const categories: Category[] = [];
  categoryMap.forEach((items, categoryId) => {
    // Find category name from mock data or use the ID
    const mockCategory = mockCategories.find(cat => cat.id === categoryId);
    categories.push({
      id: categoryId,
      name: mockCategory?.name || categoryId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      description: mockCategory?.description || `Items in ${categoryId} category`,
      items
    });
  });

  return categories;
}

export class DataService {
  private static instance: DataService;
  private isSupabaseConnected: boolean | null = null;
  private categoriesCache: Category[] | null = null;
  private ownersCache: Map<string, Owner> = new Map();

  static getInstance(): DataService {
    if (!DataService.instance) {
      DataService.instance = new DataService();
    }
    return DataService.instance;
  }

  async checkConnection(): Promise<boolean> {
    if (this.isSupabaseConnected === null) {
      this.isSupabaseConnected = await checkSupabaseConnection();
      console.log('Supabase connection status:', this.isSupabaseConnected ? 'Connected' : 'Not connected');
    }
    return this.isSupabaseConnected;
  }

  async getCategories(): Promise<Category[]> {
    if (this.categoriesCache) {
      return this.categoriesCache;
    }

    const connected = await this.checkConnection();
    
    if (connected) {
      try {
        console.log('Fetching data from Supabase...');
        const [items, rentals, users] = await Promise.all([
          fetchItemsFromSupabase(),
          fetchRentalsFromSupabase(),
          fetchUsersFromSupabase()
        ]);

        // Transform and cache users
        users.forEach(user => {
          this.ownersCache.set(user.id, transformSupabaseUserToOwner(user));
        });

        // Transform items
        const transformedItems = items.map(transformSupabaseItemToItem);

        // Add rental periods to items
        transformedItems.forEach(item => {
          const itemRentals = rentals.filter(rental => rental.item_id === item.id);
          item.rentalPeriods = itemRentals.map(rental => ({
            startDate: rental.start_date,
            endDate: rental.end_date,
            returned: rental.returned || false,
            renterId: rental.renter_id,
            renterName: this.ownersCache.get(rental.renter_id)?.name
          }));
        });

        // Group items by category
        this.categoriesCache = groupItemsByCategory(transformedItems);
        console.log(`Loaded ${transformedItems.length} items from Supabase`);
        
        return this.categoriesCache;
      } catch (error) {
        console.error('Error fetching data from Supabase, falling back to mock data:', error);
        this.categoriesCache = mockCategories;
        return this.categoriesCache;
      }
    } else {
      console.log('Using mock data (Supabase not connected)');
      this.categoriesCache = mockCategories;
      return this.categoriesCache;
    }
  }

  async getOwner(ownerId: string): Promise<Owner | null> {
    // First check cache
    if (this.ownersCache.has(ownerId)) {
      return this.ownersCache.get(ownerId)!;
    }

    const connected = await this.checkConnection();
    
    if (connected) {
      try {
        // If not in cache and we have Supabase connection, fetch all users to populate cache
        await this.getCategories(); // This will populate the owners cache
        
        // If still not found, try to create a user record
        if (!this.ownersCache.has(ownerId)) {
          console.log('Owner not found in cache, attempting to create user record:', ownerId);
          
          // Check if user exists in users table
          const { data: existingUser, error: userCheckError } = await supabase
            .from('users')
            .select('id, name, email')
            .eq('id', ownerId)
            .single();

          if (userCheckError && userCheckError.code !== 'PGRST116') { // PGRST116 is "not found"
            console.error('Error checking if user exists:', userCheckError);
            return null;
          }

          if (!existingUser) {
            // Get current user information from auth
            const currentUser = await this.getCurrentUser();
            const userName = currentUser?.user_metadata?.name || currentUser?.email?.split('@')[0] || 'User';
            const userEmail = currentUser?.email || 'user@example.com';
            
            // Create user record
            const { data: newUser, error: createUserError } = await supabase
              .from('users')
              .insert({
                id: ownerId,
                name: userName,
                email: userEmail
              })
              .select()
              .single();

            if (createUserError) {
              console.error('Error creating user record:', createUserError);
              return null;
            }

            console.log('Created user record for owner:', ownerId);
            
            // Add to cache
            const owner = transformSupabaseUserToOwner(newUser);
            this.ownersCache.set(ownerId, owner);
            return owner;
          } else {
            // User exists but wasn't in cache, add to cache
            const owner = transformSupabaseUserToOwner(existingUser);
            this.ownersCache.set(ownerId, owner);
            return owner;
          }
        }
        
        return this.ownersCache.get(ownerId) || null;
      } catch (error) {
        console.error('Error fetching owner from Supabase:', error);
        return null;
      }
    }

    return null;
  }

  // Method to clear cache and force refresh
  async refreshData(): Promise<void> {
    this.categoriesCache = null;
    this.ownersCache.clear();
    this.isSupabaseConnected = null;
    await this.getCategories();
  }

  // Method to refresh owners cache specifically
  async refreshOwnersCache(): Promise<void> {
    const connected = await this.checkConnection();
    
    if (connected) {
      try {
        const users = await fetchUsersFromSupabase();
        // Clear and repopulate owners cache
        this.ownersCache.clear();
        users.forEach(user => {
          this.ownersCache.set(user.id, transformSupabaseUserToOwner(user));
        });
        console.log('Owners cache refreshed');
      } catch (error) {
        console.error('Error refreshing owners cache:', error);
      }
    }
  }

  // Method to check if we're using Supabase data
  isUsingSupabaseData(): boolean {
    return this.isSupabaseConnected === true;
  }

  // Get current authenticated user information
  async getCurrentUser(): Promise<any> {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('Error getting current user:', error);
        return null;
      }
      return user;
    } catch (error) {
      console.error('Exception getting current user:', error);
      return null;
    }
  }

  // Get user data from the database (including profile picture)
  async getUserData(userId: string): Promise<any> {
    const connected = await this.checkConnection();
    
    if (!connected) {
      console.log('Cannot get user data: Supabase not connected');
      return null;
    }

    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user data:', error);
        return null;
      }

      console.log('Fetched user data from database:', userData);
      return userData;
    } catch (error) {
      console.error('Exception fetching user data:', error);
      return null;
    }
  }

  // Refresh user data from the database (same as getUserData but for consistency)
  async refreshUserData(userId: string): Promise<any> {
    return await this.getUserData(userId);
  }

  // Update user's phone number in the database
  async updateUserPhoneNumber(userId: string, phoneNumber: any): Promise<boolean> {
    const connected = await this.checkConnection();
    
    if (!connected) {
      console.log('Cannot update phone number: Supabase not connected');
      return false;
    }

    try {
      console.log('Updating phone number for user:', userId);
      console.log('Phone number data:', phoneNumber);

      // First, check if user exists in the users table
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('id')
        .eq('id', userId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error checking if user exists:', checkError);
        return false;
      }

      if (!existingUser) {
        // User doesn't exist, create them first
        console.log('User not found in users table, creating user record...');
        
        // Get current user information from auth
        const currentUser = await this.getCurrentUser();
        const userName = currentUser?.user_metadata?.name || currentUser?.email?.split('@')[0] || 'User';
        const userEmail = currentUser?.email || 'user@example.com';
        
        const { error: createError } = await supabase
          .from('users')
          .insert({
            id: userId,
            name: userName,
            email: userEmail
          });

        if (createError) {
          console.error('Error creating user record:', createError);
          return false;
        }

        console.log('Created user record for:', userId);
      }

      const updateData = {
        phone_number: phoneNumber.formatted,
        phone_country_code: phoneNumber.countryCode,
        phone_verified: phoneNumber.verified
      };

      console.log('Update data to be sent:', updateData);

      const { data, error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', userId)
        .select();

      if (error) {
        console.error('Error updating phone number:', error);
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        return false;
      }

      console.log('Phone number updated successfully:', data);
      return true;
    } catch (error) {
      console.error('Exception updating phone number:', error);
      console.error('Exception details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace'
      });
      return false;
    }
  }

  // Get items owned by a specific user
  async getUserItems(userId: string): Promise<Item[]> {
    const categories = await this.getCategories();
    const allItems = categories.flatMap(category => category.items);
    const userItems = allItems.filter(item => item.ownerId === userId);
    
    console.log(`Found ${userItems.length} items for user ${userId} using ${this.isUsingSupabaseData() ? 'Supabase' : 'mock'} data`);
    return userItems;
  }

  // Get rentals for a specific user (items they are renting)
  async getUserRentals(userId: string): Promise<any[]> {
    const connected = await this.checkConnection();
    
    if (connected) {
      try {
        const rentals = await fetchRentalsFromSupabase();
        const userRentals = rentals.filter(rental => rental.renter_id === userId);
        console.log(`Found ${userRentals.length} rentals for user ${userId} using Supabase data`);
        return userRentals;
      } catch (error) {
        console.error('Error fetching user rentals from Supabase:', error);
        return [];
      }
    } else {
      // Return empty array when using mock data mode
      console.log(`No rentals found for user ${userId} (using mock data mode)`);
      return [];
    }
  }

  // Get notifications for a specific user
  async getUserNotifications(userId: string): Promise<any[]> {
    const connected = await this.checkConnection();
    
    if (connected) {
      try {
        // In a real implementation, you'd fetch from a notifications table
        // For now, we'll derive notifications from items and rentals
        const userItems = await this.getUserItems(userId);
        const userRentals = await this.getUserRentals(userId);
        
        // Convert to notification format
        const notifications = [
          ...userItems.map(item => ({
            type: 'item',
            item,
            unreadCount: 0 // Would come from messages table
          })),
          ...userRentals.map(rental => ({
            type: 'rental',
            rental,
            unreadCount: 0 // Would come from messages table
          }))
        ];
        
        console.log(`Found ${notifications.length} notifications for user ${userId} using Supabase data`);
        return notifications;
      } catch (error) {
        console.error('Error fetching user notifications from Supabase:', error);
        return [];
      }
    } else {
      // Return empty array when using mock data mode
      console.log(`No notifications found for user ${userId} (using mock data mode)`);
      return [];
    }
  }

  // Add a new item to the data source
  async addItem(item: Item): Promise<boolean> {
    const connected = await this.checkConnection();
    
    if (connected) {
      try {
        // Test if we can read from the items table
        const { data: testData, error: testError } = await supabase
          .from('items')
          .select('id')
          .limit(1);
        
        if (testError) {
          console.error('Cannot read from items table:', testError);
          return false;
        }
        
        // Validate required fields
        if (!item.name || !item.ownerId || !item.location || !item.category) {
          console.error('Missing required fields:', {
            name: !!item.name,
            ownerId: !!item.ownerId,
            location: !!item.location,
            category: !!item.category
          });
          return false;
        }

        // Validate location has required fields
        if (!item.location.address || !item.location.city || !item.location.postcode) {
          console.error('Location missing required fields:', {
            address: !!item.location.address,
            city: !!item.location.city,
            postcode: !!item.location.postcode
          });
          return false;
        }

        // Geocode the address to get coordinates
        let geocodedLocation: Location;
        try {
          geocodedLocation = await geocodeAddress(item.location);
          console.log('Geocoding successful:', geocodedLocation.coordinates);
        } catch (geocodingError) {
          console.error('Geocoding failed:', geocodingError);
          return false;
        }

        // Validate that coordinates were obtained
        if (!geocodedLocation.coordinates || 
            typeof geocodedLocation.coordinates.latitude !== 'number' || 
            typeof geocodedLocation.coordinates.longitude !== 'number') {
          console.error('Invalid coordinates obtained from geocoding');
          return false;
        }

        // Check if owner exists in users table, create if not
        const { data: existingUser, error: userCheckError } = await supabase
          .from('users')
          .select('id')
          .eq('id', item.ownerId)
          .single();

        if (userCheckError && userCheckError.code !== 'PGRST116') { // PGRST116 is "not found"
          console.error('Error checking if user exists:', userCheckError);
          return false;
        }

        if (!existingUser) {
          // Get current user information from auth
          const currentUser = await this.getCurrentUser();
          const userName = currentUser?.user_metadata?.name || currentUser?.email?.split('@')[0] || 'User';
          const userEmail = currentUser?.email || 'user@example.com';
          
          // Create user record
          const { error: createUserError } = await supabase
            .from('users')
            .insert({
              id: item.ownerId,
              name: userName,
              email: userEmail
            });

          if (createUserError) {
            console.error('Error creating user record:', createUserError);
            return false;
          }

          console.log('Created user record for owner:', item.ownerId);
        }
        
        // Transform item to Supabase format with geocoded location
        const supabaseItem = {
          name: item.name,
          description: item.description,
          daily_rate_gbp: item.dailyRateGBP,
          images: item.images && item.images.length > 0 ? item.images : null, // Handle empty images array
          owner_id: item.ownerId,
          min_days: item.minDays,
          max_days: item.maxDays,
          location: JSON.stringify(geocodedLocation), // Serialize geocoded location as JSON
          category: item.category,
          availability: item.availability
        };

        const { data, error } = await supabase.from('items').insert(supabaseItem);
        
        if (error) {
          console.error('Supabase error details:', error);
          console.error('Error code:', error.code);
          console.error('Error message:', error.message);
          console.error('Error details:', error.details);
          console.error('Error hint:', error.hint);
          return false;
        }

        // Clear cache to force refresh
        this.categoriesCache = null;
        await this.refreshOwnersCache(); // Refresh owners cache to include new user
        console.log('Item added to Supabase successfully');
        return true;
      } catch (error) {
        console.error('Exception in addItem:', error);
        console.error('Error type:', typeof error);
        console.error('Error stack:', error instanceof Error ? error.stack : 'No stack trace');
        return false;
      }
    } else {
      // For mock data mode, geocode and add to local cache
      console.log('Adding item to mock data cache:', item);
      
      // Validate location has required fields
      if (!item.location.address || !item.location.city || !item.location.postcode) {
        console.error('Location missing required fields for mock data');
        return false;
      }

      // Geocode the address even for mock data
      let geocodedLocation: Location;
      try {
        geocodedLocation = await geocodeAddress(item.location);
        console.log('Geocoding successful for mock data:', geocodedLocation.coordinates);
      } catch (geocodingError) {
        console.error('Geocoding failed for mock data:', geocodingError);
        return false;
      }

      // Create item with geocoded location
      const itemWithGeocodedLocation: Item = {
        ...item,
        location: geocodedLocation
      };

      if (this.categoriesCache) {
        const category = this.categoriesCache.find(cat => cat.id === item.category);
        if (category) {
          category.items.push(itemWithGeocodedLocation);
        } else {
          // Create new category if it doesn't exist
          this.categoriesCache.push({
            id: item.category,
            name: item.category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
            description: `Items in ${item.category} category`,
            items: [itemWithGeocodedLocation]
          });
        }
      }
      console.log('Item added to mock data cache');
      return true;
    }
  }

  // Delete an item from the data source
  async deleteItem(itemId: string): Promise<boolean> {
    const connected = await this.checkConnection();
    
    if (connected) {
      try {
        const { error } = await supabase
          .from('items')
          .delete()
          .eq('id', itemId);
        
        if (error) {
          console.error('Error deleting item from Supabase:', error);
          return false;
        }

        // Clear cache to force refresh
        this.categoriesCache = null;
        console.log('Item deleted from Supabase successfully');
        return true;
      } catch (error) {
        console.error('Exception in deleteItem:', error);
        return false;
      }
    } else {
      // For mock data mode, remove from local cache
      console.log('Deleting item from mock data cache:', itemId);
      if (this.categoriesCache) {
        this.categoriesCache.forEach(category => {
          category.items = category.items.filter(item => item.id !== itemId);
        });
        // Remove empty categories
        this.categoriesCache = this.categoriesCache.filter(category => category.items.length > 0);
      }
      console.log('Item deleted from mock data cache');
      return true;
    }
  }

  // Update an existing item in the data source
  async updateItem(item: Item): Promise<boolean> {
    const connected = await this.checkConnection();
    
    if (connected) {
      try {
        // Validate location has required fields
        if (!item.location.address || !item.location.city || !item.location.postcode) {
          console.error('Location missing required fields for update:', {
            address: !!item.location.address,
            city: !!item.location.city,
            postcode: !!item.location.postcode
          });
          return false;
        }

        // Geocode the address to get coordinates
        let geocodedLocation: Location;
        try {
          geocodedLocation = await geocodeAddress(item.location);
          console.log('Geocoding successful for update:', geocodedLocation.coordinates);
        } catch (geocodingError) {
          console.error('Geocoding failed for update:', geocodingError);
          return false;
        }

        // Validate that coordinates were obtained
        if (!geocodedLocation.coordinates || 
            typeof geocodedLocation.coordinates.latitude !== 'number' || 
            typeof geocodedLocation.coordinates.longitude !== 'number') {
          console.error('Invalid coordinates obtained from geocoding for update');
          return false;
        }

        // Check if owner exists in users table, create if not
        const { data: existingUser, error: userCheckError } = await supabase
          .from('users')
          .select('id')
          .eq('id', item.ownerId)
          .single();

        if (userCheckError && userCheckError.code !== 'PGRST116') { // PGRST116 is "not found"
          console.error('Error checking if user exists for update:', userCheckError);
          return false;
        }

        if (!existingUser) {
          // Get current user information from auth
          const currentUser = await this.getCurrentUser();
          const userName = currentUser?.user_metadata?.name || currentUser?.email?.split('@')[0] || 'User';
          const userEmail = currentUser?.email || 'user@example.com';
          
          // Create user record
          const { error: createUserError } = await supabase
            .from('users')
            .insert({
              id: item.ownerId,
              name: userName,
              email: userEmail
            });

          if (createUserError) {
            console.error('Error creating user record for update:', createUserError);
            return false;
          }

          console.log('Created user record for owner during update:', item.ownerId);
        }

        // Transform item to Supabase format with geocoded location
        const supabaseItem = {
          name: item.name,
          description: item.description,
          daily_rate_gbp: item.dailyRateGBP,
          images: item.images && item.images.length > 0 ? item.images : null,
          owner_id: item.ownerId,
          min_days: item.minDays,
          max_days: item.maxDays,
          location: JSON.stringify(geocodedLocation),
          category: item.category,
          availability: item.availability
        };

        const { error } = await supabase
          .from('items')
          .update(supabaseItem)
          .eq('id', item.id);
        
        if (error) {
          console.error('Error updating item in Supabase:', error);
          return false;
        }

        // Clear cache to force refresh
        this.categoriesCache = null;
        await this.refreshOwnersCache(); // Refresh owners cache to include new user
        console.log('Item updated in Supabase successfully');
        return true;
      } catch (error) {
        console.error('Exception in updateItem:', error);
        return false;
      }
    } else {
      // For mock data mode, geocode and update in local cache
      console.log('Updating item in mock data cache:', item.id);
      
      // Validate location has required fields
      if (!item.location.address || !item.location.city || !item.location.postcode) {
        console.error('Location missing required fields for mock data update');
        return false;
      }

      // Geocode the address even for mock data
      let geocodedLocation: Location;
      try {
        geocodedLocation = await geocodeAddress(item.location);
        console.log('Geocoding successful for mock data update:', geocodedLocation.coordinates);
      } catch (geocodingError) {
        console.error('Geocoding failed for mock data update:', geocodingError);
        return false;
      }

      // Create item with geocoded location
      const itemWithGeocodedLocation: Item = {
        ...item,
        location: geocodedLocation
      };

      if (this.categoriesCache) {
        this.categoriesCache.forEach(category => {
          const itemIndex = category.items.findIndex(cacheItem => cacheItem.id === item.id);
          if (itemIndex !== -1) {
            category.items[itemIndex] = itemWithGeocodedLocation;
          }
        });
      }
      console.log('Item updated in mock data cache');
      return true;
    }
  }

  // Get reviews for a specific user
  async getUserReviews(userId: string): Promise<Review[]> {
    const connected = await this.checkConnection();
    
    if (connected) {
      try {
        const { data: reviews, error } = await supabase
          .from('reviews')
          .select(`
            *,
            items!reviews_item_id_fkey(name)
          `)
          .or(`reviewer_id.eq.${userId},reviewee_id.eq.${userId}`);

        if (error) {
          console.error('Error fetching user reviews from Supabase:', error);
          return [];
        }

        // Transform to Review format
        const transformedReviews: Review[] = (reviews || []).map(review => ({
          id: review.id,
          rating: review.rating,
          comment: review.comment || '',
          reviewerName: review.reviewer_id === userId ? 'You' : 'Reviewer', // We'd need to join with users table for real names
          reviewerId: review.reviewer_id,
          date: review.created_at || new Date().toISOString().split('T')[0],
          itemId: review.item_id,
          itemName: review.items?.name || 'Unknown Item'
        }));

        console.log(`Found ${transformedReviews.length} reviews for user ${userId} using Supabase data`);
        return transformedReviews;
      } catch (error) {
        console.error('Error fetching user reviews from Supabase:', error);
        return [];
      }
    } else {
      // Return empty array when using mock data mode
      console.log(`No reviews found for user ${userId} (using mock data mode)`);
      return [];
    }
  }

  // Get transactions derived from rentals (since we don't have a transactions table)
  async getUserTransactions(userId: string): Promise<Transaction[]> {
    const connected = await this.checkConnection();
    
    if (connected) {
      try {
        // Get rentals where user is either the renter or the owner
        const { data: rentals, error } = await supabase
          .from('rentals')
          .select(`
            *,
            items!rentals_item_id_fkey(name, daily_rate_gbp)
          `)
          .or(`renter_id.eq.${userId},owner_id.eq.${userId}`);

        if (error) {
          console.error('Error fetching user rentals for transactions:', error);
          return [];
        }

        // Transform rentals to transaction format
        const transactions: Transaction[] = (rentals || []).map(rental => {
          const isRenter = rental.renter_id === userId;
          const serviceFee = Math.round(rental.total_cost * 0.1); // 10% service fee
          
          return {
            id: `transaction-${rental.id}`,
            date: rental.created_at || new Date().toISOString().split('T')[0],
            amount: rental.total_cost,
            paymentMethod: 'card',
            status: rental.returned ? 'completed' : 'pending',
            type: isRenter ? 'sent' : 'received',
            itemId: rental.item_id,
            itemName: rental.items?.name || 'Unknown Item',
            counterpartyId: isRenter ? rental.owner_id : rental.renter_id,
            counterpartyName: isRenter ? 'Item Owner' : 'Renter', // We'd need to join with users table for real names
            serviceFee,
            totalAmount: rental.total_cost + serviceFee
          };
        });

        console.log(`Found ${transactions.length} transactions for user ${userId} using Supabase data`);
        return transactions;
      } catch (error) {
        console.error('Error fetching user transactions from Supabase:', error);
        return [];
      }
    } else {
      // Return empty array when using mock data mode
      console.log(`No transactions found for user ${userId} (using mock data mode)`);
      return [];
    }
  }

  // Upload profile picture and update user record
  async uploadProfilePicture(userId: string, file: File): Promise<string | null> {
    const connected = await this.checkConnection();
    
    if (!connected) {
      console.error('Cannot upload profile picture: Supabase not connected');
      return null;
    }

    try {
      console.log('Starting profile picture upload for user:', userId);
      console.log('File details:', { name: file.name, size: file.size, type: file.type });
      
      // Generate unique filename with simpler path structure
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      
      console.log('Generated file name:', fileName);

      // Upload file to Supabase storage with simpler path
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Error uploading profile picture to storage:', uploadError);
        console.error('Error details:', {
          message: uploadError.message
        });
        return null;
      }

      console.log('File uploaded successfully to storage');

      // Get public URL for the uploaded file
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      const publicUrl = urlData.publicUrl;
      console.log('Generated public URL:', publicUrl);

      // Update user record with new picture URL
      const { error: updateError } = await supabase
        .from('users')
        .update({ picture: publicUrl })
        .eq('id', userId);

      if (updateError) {
        console.error('Error updating user picture in database:', updateError);
        console.error('Update error details:', {
          message: updateError.message,
          code: updateError.code,
          details: updateError.details
        });
        // Try to delete the uploaded file if user update fails
        await supabase.storage.from('avatars').remove([fileName]);
        return null;
      }

      console.log('Profile picture uploaded successfully:', publicUrl);
      return publicUrl;
    } catch (error) {
      console.error('Exception uploading profile picture:', error);
      console.error('Exception details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace'
      });
      return null;
    }
  }
}

// Export singleton instance
export const dataService = DataService.getInstance(); 